"use client";
import {
  Box,
  Center,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import PricingFooter from "@/components/pricingFooter";
import {useSectionHeadingVariant} from "@/theme/components/useSectionHeading";

type ServerTableData = {
  sku: string;
  cpu: string;
  memory: string;
  user_disk: string;
  pricing: {
    linux: string;
    windows: string;
  };
};

type ServerTableProps = {
  heading: string;
  showHeader?: boolean;
  data: ServerTableData[];
  footer?: boolean;
};

const FOOTER_CONSTANTS = {
  btnText: "Test Drive our VDI Solution",
};

export const DesktopView = ({data}: {data: ServerTableData[]}) => {
  return (
    <Box>
      <TableContainer>
        <Table variant="striped" border="2px solid rgb(237,242,247)" width={"100%"}>
          <Thead>
            <Tr>
              <Th rowSpan={2} textAlign={"center"}>
                SKU
              </Th>
              <Th rowSpan={2} textAlign={"center"} textTransform={"none"}>
                vCPU
              </Th>
              <Th rowSpan={2} textAlign={"center"}>
                Memory
              </Th>
              <Th rowSpan={2} textAlign={"center"}>
                User Disk
              </Th>
              <Th colSpan={2} textAlign={"center"} border="2px solid #EDF2F7">
                Pricing / month
              </Th>
            </Tr>
            <Tr>
              <Th textAlign={"center"} border="2px solid #EDF2F7">
                Linux
              </Th>{" "}
              <Th textAlign={"center"} border="2px solid #EDF2F7">
                Windows
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map((server, index) => (
              <Tr key={index}>
                <Td textAlign={"center"}>{server.sku}</Td>
                <Td textAlign={"center"}>{server.cpu}</Td>
                <Td textAlign={"center"}>{server.memory}</Td>
                <Td textAlign={"center"}>{server.user_disk}</Td>
                <Td
                  textAlign={"center"}
                  fontWeight={"700"}
                  fontSize={"18px"}
                  color="blue.600"
                >
                  {server.pricing.linux}
                </Td>
                <Td
                  textAlign={"center"}
                  fontWeight={"700"}
                  fontSize={"18px"}
                  color="blue.600"
                >
                  {server.pricing.windows}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const MobileView = ({data}: {data: ServerTableData[]}) => {
  return (
    <SimpleGrid columns={2} spacing={6}>
      {data.map((plan, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          spacing={4}
          w="100%"
          p={4}
          bg="white"
        >
          <Flex direction="column" w="full">
            <Text fontWeight="bold" fontSize="lg">
              {plan.sku}
            </Text>
          </Flex>

          <Divider borderColor="gray.200" />

          <Stack spacing={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                CPU : {""}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.cpu}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Memory : {""}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.memory}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                User Disk : {""}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.user_disk}
              </Text>
            </Box>
          </Stack>

          <Divider borderColor="gray.200" />

          <Stack spacing={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Linux Pricing : {""}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="blue.600">
                {plan.pricing.linux} /mo
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Windows Pricing : {""}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="blue.600">
                {plan.pricing.windows} /mo
              </Text>
            </Box>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const OrganizationServerTable = (props: ServerTableProps) => {
  const {showHeader, heading, data, footer} = props;
  const HeadingVariant = useSectionHeadingVariant();
  return (
    <Box id="dedicated-server-for-organization">
      {showHeader && (
        <Heading variant={HeadingVariant} textAlign={"center"} mb={8}>
          {heading}
        </Heading>
      )}
      <Center display={{md: "none"}} w="full">
        <MobileView data={data} />
      </Center>
      <Box display={{base: "none", md: "block"}}>
        <DesktopView data={data} />
      </Box>
      {footer && <PricingFooter {...FOOTER_CONSTANTS} />}
      <Divider borderWidth="2px" borderColor="#ECECEC" my={10} />
    </Box>
  );
};

export default OrganizationServerTable;
